import React, { useCallback, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import cs from "classnames";
import { usePopper } from "react-popper";
import DropDownItem from "./DropDownItem";
import useOutsideClick from "../../../hooks/useOutsideClick";

import styles from "./DropDown.module.scss";

const setPlacement = (position, align) => {
  const alignment = align ? `-${align}` : "";
  return `${position || "auto"}${alignment}`;
};

const defaultOptions = [{ name: "offset", options: { offset: [20, -25] } }];

const DropDown = ({
  referenceElement,
  children,
  className,
  options,
  popperOptions,
  popperPosition,
  popperAlign,
  onSelect,
  closeOnSelect,
  ...rest
}) => {
  const [popperElement, setPopperElement] = useState(null);
  const { styles: PopperStyles, attributes } = usePopper(referenceElement, popperElement, {
    placement: setPlacement(popperPosition, popperAlign),
    modifiers: [...defaultOptions, ...popperOptions]
  });

  const hide = useCallback(() => {
    popperElement.removeAttribute("data-show");
    referenceElement.removeAttribute("data-active", "");
  }, [popperElement, referenceElement]);

  const toggleMenu = useCallback(() => {
    if (referenceElement.hasAttribute("data-active")) {
      hide();
    } else {
      popperElement.setAttribute("data-show", "");
      referenceElement.setAttribute("data-active", "");
    }
  }, [popperElement, referenceElement, hide]);

  const handleClick = useCallback(() => {
    if (closeOnSelect) {
      hide();
    }
  }, [closeOnSelect, hide]);

  useOutsideClick([popperElement, referenceElement], () => {
    hide();
  });

  useEffect(() => {
    referenceElement.addEventListener("click", toggleMenu);

    return () => {
      referenceElement.removeEventListener("click", toggleMenu);
    };
  }, [referenceElement, toggleMenu]);

  const itemClick = useMemo(() => {
    return onSelect ? { onClick: onSelect } : {};
  }, [onSelect]);

  return (
    <div
      className={cs(styles.dropDown, className)}
      ref={setPopperElement}
      style={PopperStyles.popper}
      {...attributes.popper}
      {...rest}
    >
      {options &&
        options.map(option => (
          <DropDownItem
            value={option.value}
            className={option.className}
            handleClose={handleClick}
            key={option.value}
            {...itemClick}
            {...option}
          >
            {option.label}
          </DropDownItem>
        ))}
      {React.Children.map(children, child =>
        React.cloneElement(child, { onClick: child.props.onClick || onSelect, handleClose: handleClick })
      )}
    </div>
  );
};

DropDown.displayName = "DropDown";

DropDown.defaultProps = {
  children: undefined,
  className: undefined,
  options: undefined,
  popperOptions: [{}],
  popperPosition: "left",
  popperAlign: "end",
  closeOnSelect: true,
  onSelect: undefined
};

DropDown.propTypes = {
  referenceElement: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.element })]).isRequired,
  children: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired
    }).isRequired
  ),
  popperOptions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      options: PropTypes.object
    })
  ),
  closeOnSelect: PropTypes.bool,
  onSelect: PropTypes.func,
  className: PropTypes.string,
  popperPosition: PropTypes.string,
  popperAlign: PropTypes.string
};

export default DropDown;
